/** @file Prefill of each generated Inspector form from the entity it edits (in the command's input shape). */
import type { AttachmentDoc, Doc, Id } from '@pose2d/format/types';
import type { ENTITY_FORMS } from './entityForms.data';
import type { FormKind } from './formKindOf';

/** The input key naming the entity (hidden in the form; null for project forms) and the prefilled input. */
export interface Prefill {
  identity: string | null;
  values: Record<string, unknown>;
}

type Reader = (doc: Doc, id: Id) => Record<string, unknown>;
type Entry = [identity: string | null, read: Reader];
/** A command some generated Inspector form runs: a reader for any other command is a type error. */
type FormCommand = (typeof ENTITY_FORMS)[FormKind]['forms'][number];

const pick = (source: object | undefined, keys: readonly string[]): Record<string, unknown> =>
  Object.fromEntries(keys.filter((k) => source !== undefined && k in source).map((k) => [k, (source as Record<string, unknown>)[k]]));
const region = (att: AttachmentDoc | undefined): object | undefined => (att?.type === 'region' ? att : undefined);
const mesh = (att: AttachmentDoc | undefined) => (att?.type === 'mesh' ? att : undefined);

const READERS: Readonly<Partial<Record<FormCommand, Entry>>> = {
  'slot.setColor': ['target', (d, id) => ({ value: { color: d.slots[id]?.setup.color, dark: d.slots[id]?.setup.dark } })],
  'slot.setBlend': ['target', (d, id) => ({ value: d.slots[id]?.blend })],
  'slot.setAttachment': ['target', (d, id) => ({ value: d.slots[id]?.setup.attachment })],
  'slot.setBone': ['target', (d, id) => ({ bone: d.slots[id]?.bone })],
  'attachment.setTransform': ['target', (d, id) => ({ value: pick(region(d.attachments[id]), ['x', 'y', 'rotation', 'scaleX', 'scaleY', 'width', 'height']) })],
  'attachment.setImage': ['target', (d, id) => ({ image: d.attachments[id]?.image })],
  'attachment.setHandle': ['target', (d, id) => ({ value: d.attachments[id]?.handle ?? null })],
  'mesh.setBinding': ['target', (d, id) => ({ binding: mesh(d.attachments[id])?.geometry.kind })],
  'mesh.setHull': ['target', (d, id) => ({ hull: mesh(d.attachments[id])?.hull })],
  'project.setFps': [null, (d) => ({ fps: d.meta.fps })],
  'project.setView': [null, (d) => ({ grid: d.view.grid })],
};

/** Prefill for `command` on entity `id`; an unlisted command gets `{ target: id }` (smoothWeights, rebindMesh). */
export function entityPrefill(command: string, doc: Doc, id: Id): Prefill {
  const entry = (READERS as Readonly<Record<string, Entry | undefined>>)[command];
  if (entry === undefined) return { identity: 'target', values: { target: id } };
  const [identity, read] = entry;
  return { identity, values: identity === null ? read(doc, id) : { [identity]: id, ...read(doc, id) } };
}
